import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  type Edge,
} from "@xyflow/react";
import type { TransitionCondition } from "@/types/activity";

export type TransitionEdgeData = {
  condition: TransitionCondition | null;
  orderIndex: number;
  /** Labels of selected response values (for display on the edge) */
  responseLabels?: string[];
};

type TransitionEdgeType = Edge<TransitionEdgeData, "transitionEdge">;

function conditionLabel(condition: TransitionCondition | null): string {
  if (!condition) return "";
  const ops: Record<string, string> = { lt: "<", lte: "≤", gt: ">", gte: "≥", eq: "=" };
  if (condition.type === "score") {
    const source = condition.card_group_id ? "Σ Groupe" : condition.card_id ? "Carte" : "Score";
    return `${source} ${ops[condition.operator] || condition.operator} ${condition.value}`;
  }
  if (condition.type === "score_comparison") {
    return `Score A ${ops[condition.operator] || condition.operator} Score B`;
  }
  if (condition.type === "response_value") {
    return ""; // handled separately with labels
  }
  if (condition.type === "compound") {
    const logic = condition.logic === "and" ? " ET " : " OU ";
    const labels = condition.conditions
      .map((c) => conditionLabel(c))
      .filter(Boolean);
    return labels.length > 0 ? labels.join(logic) : "";
  }
  return "";
}

export default function TransitionEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
}: EdgeProps<TransitionEdgeType>) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const scoreLabel = data ? conditionLabel(data.condition) : "";
  const responseLabel = data?.responseLabels?.length
    ? `Réponse = ${data.responseLabels.join(", ")}`
    : "";
  const label = scoreLabel || responseLabel;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: selected ? "hsl(var(--primary))" : "hsl(var(--border))",
          strokeWidth: selected ? 2.5 : 2,
        }}
      />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            className={`
              text-[10px] px-2 py-0.5 rounded-full border shadow-sm
              ${selected ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border"}
            `}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
